import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { omit, pick } from 'lodash';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as SS from 'styled-system';
import { isIE } from './constants'; // This file exists, in part, because styled-components types are a PAIN.

// This works around the `babel-plugin-styled-components` `displayName` setting,
// so we can specify the CSS class of the resulting component.
export var styledWithClass = function styledWithClass(component, className) {
  var tag = /*#__PURE__*/styled(component);
  return tag.withConfig({
    componentId: className
  });
}; // This gives us a styled component without the polymorphic behavior: the `as`
// attr overrides any `as` prop passed in. Unfortunately I can't think of a way
// to make Typescript consider it an invalid prop as well.

export var styledUnpoly = function styledUnpoly(component) {
  var fixed = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : component;
  var tag = /*#__PURE__*/styled(component).attrs({
    as: fixed
  });

  if ('displayName' in fixed) {
    tag = tag.withConfig({
      displayName: fixed.displayName
    });
  }

  return tag;
};
var cssVal = PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired;
export var styledProp = PropTypes.oneOfType([cssVal, PropTypes.arrayOf(cssVal)]);
export var classes = function classes() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  return args.filter(Boolean).join(' ');
};
export var pickStyles = function pickStyles(styles) {
  if (!styles.config) return styles;

  for (var _len2 = arguments.length, keys = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    keys[_key2 - 1] = arguments[_key2];
  }

  return SS.createParser(pick(styles.config, keys));
};
export var omitStyles = function omitStyles(styles) {
  if (!styles.config) return styles;

  for (var _len3 = arguments.length, keys = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    keys[_key3 - 1] = arguments[_key3];
  }

  return SS.createParser(omit(styles.config, keys));
}; // Keeping this separate from `flexContainer` styles because it applies
// to the element's children, not the element itself.

export var gapWorkaround = function (_CSS$supports, _CSS) {
  if (typeof CSS !== 'undefined' && (_CSS$supports = (_CSS = CSS).supports) !== null && _CSS$supports !== void 0 && _CSS$supports.call(_CSS, 'gap', '1px')) return;

  var calcGap = function calcGap(value, scale) {
    try {
      var _scale$value;

      value = (_scale$value = scale === null || scale === void 0 ? void 0 : scale[value]) !== null && _scale$value !== void 0 ? _scale$value : value;
    } catch (_unused) {}

    if (typeof value === 'number') {
      return value / 2;
    } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
      return "calc(".concat(value.trim(), " / 2)");
    }

    return value;
  };

  var makeGapParser = function makeGapParser() {
    for (var _len4 = arguments.length, props = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      props[_key4] = arguments[_key4];
    }

    var parser = function parser(value, scale) {
      var styles = {};
      var gap = calcGap(value, scale);

      for (var _i = 0, _props = props; _i < _props.length; _i++) {
        var prop = _props[_i];
        styles[prop] = gap;
      }

      return {
        '&&&& > *': styles
      };
    };

    parser.scale = 'space';
    return parser;
  };

  return SS.system({
    gap: makeGapParser('margin'),
    rowGap: makeGapParser('marginTop', 'marginBottom'),
    colGap: makeGapParser('marginLeft', 'marginRight')
  });
}(); // IE doesn't support `space-evenly`, but you can fake it using pseudo-elements.

var spaceEvenlyFix = function () {
  if (!isIE) return;

  var justifyContent = function justifyContent(value) {
    if (!value) return;
    var placeholderBlock = {
      content: "''",
      display: 'none',
      flex: '0 0 0'
    };
    var styles = {
      justifyContent: value,
      '&::before': placeholderBlock,
      '&::after': placeholderBlock
    };

    if (value === 'space-evenly') {
      placeholderBlock.display = 'block';
      styles.justifyContent = 'space-between';
    }

    return styles;
  };

  return {
    justifyContent: justifyContent
  };
}();

var flexKeys = ['alignItems', 'alignContent', 'justifyContent', 'flexWrap', 'flexDirection'];
var itemKeys = ['flex', 'flexGrow', 'flexShrink', 'flexBasis', 'alignSelf', 'order'];
export var flexContainer = SS.system(_objectSpread(_objectSpread(_objectSpread({}, pick(SS.flexbox.config, flexKeys)), spaceEvenlyFix), {}, {
  gap: {
    property: 'gap',
    scale: 'space'
  },
  rowGap: {
    property: 'rowGap',
    scale: 'space'
  },
  colGap: {
    property: 'columnGap',
    scale: 'space'
  },
  flexFlow: true
}));
export var flexItem = pickStyles.apply(void 0, [SS.flexbox].concat(itemKeys)); // Not exhaustive, but all possible values include at least one of these words.

var isFlexKey = RegExp.prototype.test.bind(/row|column|reverse|wrap/); // Just like `flexContainer`, except it expects the default display mode to be
// something other than flex and has a shortcut to "activate" flex behavior:
// `<C flexFlow>` -> `<C display="flex" flexFlow="row nowrap">`.
// Be careful that it doesn't override another display mode, e.g. `inline-flex`.

export var flexContainerOption = SS.system(_objectSpread(_objectSpread({}, flexContainer.config), {}, {
  flexFlow: function flexFlow(value) {
    if (typeof value === 'boolean') {
      return value ? {
        display: 'flex'
      } : undefined;
    } else if (isFlexKey(value)) {
      return {
        display: 'flex',
        flexFlow: value
      };
    }
  }
}));
export var allWidth = SS.compose(SS.width, SS.minWidth, SS.maxWidth);
export var allHeight = SS.compose(SS.height, SS.minHeight, SS.maxHeight);
export var sizing = SS.compose(SS.width, SS.minWidth, SS.maxWidth, SS.height, SS.minHeight, SS.maxHeight);
/******* Text Styling *******/

var textDecoration = SS.system({
  textDecoration: true,
  textDecorationColor: {
    property: 'textDecorationColor',
    scale: 'colors'
  },
  textDecorationLine: true,
  textDecorationSkipInk: true,
  textDecorationStyle: true,
  textDecorationThickness: {
    property: 'textDecorationThickness',
    scale: 'space'
  },
  textUnderlineOffset: {
    property: 'textUnderlineOffset',
    scale: 'space'
  },
  textUnderlinePosition: true
});
var textDirection = SS.system({
  textOrientation: true,
  writingMode: true
});
var textDisplay = SS.system({
  fontStretch: true,
  fontVariant: true,
  fontVariantCaps: true,
  fontVariantLigatures: true,
  fontVariantNumeric: true,
  textIndent: {
    property: 'textIndent',
    scale: 'space',
    // Unlike most other space props, it makes sense to allow negative numbers.
    transform: function transform(value, scale) {
      if (scale) {
        var _scale$value2;

        if (typeof value === 'number' && value < 0) {
          var scaleVal = scale[-value];

          if (typeof scaleVal === 'number') {
            return -scaleVal;
          } else if (scaleVal) {
            return "-".concat(scaleVal);
          }
        }

        return (_scale$value2 = scale[value]) !== null && _scale$value2 !== void 0 ? _scale$value2 : value;
      }

      return value;
    }
  },
  textTransform: true
});
var textOverflow = SS.compose(SS.overflow, SS.overflowX, SS.overflowY, SS.system({
  hyphens: true,
  overflowWrap: {
    properties: ['overflowWrap', 'wordWrap']
  },
  textOverflow: true,
  whiteSpace: true,
  wordBreak: true
}));
// This one doesn't really fit anywhere else...
var userSelect = SS.system({
  userSelect: true
}); // Other text-related props I'm not including:
// `textJustify`: lack of browser support
// `fontVariantAlternates`: lack of browser support
// `fontVariantPosition`: lack of browser support
// `fontVariantAlternates`: lack of browser support
// `fontVariationSettings`: common use cases covered by other properties
// `textShadow`: requires custom transform to utilize themed shadows, wait for use case to add

export var allText = SS.compose(SS.color, SS.typography, textDecoration, textDirection, textDisplay, textOverflow, userSelect);